import { fetchApi } from '@/services/api-client';

import type { Teacher } from '../teachers/teachers';
import type { UserAdminRow } from '../users/users';

export const STAFF_PATH = '/staff';
export const ADMIN_STAFF_PATH = '/admin/staff';

/** Picker source for staff-user teachers (timetable cell / assistants / homeroom). */
export type StaffTeacher = {
  id: string;
  displayName: string;
  /** Preferred short name, when the staff member has one. */
  nickname: string | null;
  photoUrl: string;
  role: string;
  /** The user's current default room (used to resolve a lesson's room when no explicit room is set). */
  defaultRoomId: string | null;
  defaultRoomName: string | null;
};

/** `GET /staff/:userId`. The personal details are absent unless the viewer holds `users`. */
export type StaffDetail = UserAdminRow & {
  defaultRoomId: string | null;
  defaultRoomName: string | null;
  dateOfBirth?: string | null;
  sex?: 'male' | 'female' | null;
  phone?: string | null;
  address?: string | null;
  doorId?: string | null;
};

export type StaffOption = { id: string; displayName: string; email: string; role: 'admin' | 'teacher' | 'employee' };

export const staffApi = {
  getUser: (userId: string) => fetchApi<StaffDetail>(`${STAFF_PATH}/${userId}`),
  getProfile: (userId: string) => fetchApi<Teacher | null>(`${STAFF_PATH}/${userId}/profile`),
  teachingOptions: () => fetchApi<StaffTeacher[]>(`${ADMIN_STAFF_PATH}/teaching-options`),
  setDefaultRoom: (id: string, roomId: string | null) =>
    fetchApi<{ id: string; displayName: string; defaultRoomId: string | null }>(
      `${ADMIN_STAFF_PATH}/${id}/default-room`,
      { method: 'PATCH', body: JSON.stringify({ roomId }) }
    ),
  /** Employees only on request: every teaching picker must never be offered one. */
  listOptions: (includeEmployees = false) =>
    fetchApi<StaffOption[]>(`${STAFF_PATH}/options${includeEmployees ? '?includeEmployees=true' : ''}`),
};
